/**
 * AuthProvider — the platform-console authentication state machine.
 *
 * The console is a SEPARATE auth realm from the client backoffice and MFA is
 * mandatory. The flow, in order:
 *
 *   1. On load, `getCurrentUser()` resolves the current auth user (or none).
 *   2. If signed in, look up the matching `platform_users` row by
 *      `auth_user_id`. No row → NOT a platform super-user → sign out.
 *   3. MFA gate:
 *        - `mfa_enabled === false` → enrollment flow (mfa-enroll → mfa-verify).
 *        - `mfa_enabled === true`  → require a fresh TOTP code (mfa-verify)
 *          before granting access.
 *      Once cleared we drop a 30-day "trusted device" cookie (see deviceTrust.ts)
 *      bound to this platform user, so reloads on the same device skip the
 *      challenge. An explicit signOut clears that trust. A device WITHOUT the
 *      cookie is always re-challenged.
 *   4. Authenticated routes are gated on `user && platformUser && mfaPassed`.
 *
 * The super-user has NO `public.users` row by design — only a `platform_users`
 * row — so we never read tenant-scoped tables here (multitenancy invariant).
 */

import React from 'react';
import type { UserSchema } from '@insforge/sdk';
import { insforge } from '../lib/insforge.js';
import type { PlatformUser } from '../lib/types.js';
import { isDeviceTrusted, trustDevice } from './deviceTrust.js';

/** Authn user as returned by the SDK. */
export type AuthUser = UserSchema;

/** Result of a sign-in attempt surfaced to the login screen. */
export interface SignInResult {
  readonly ok: boolean;
  /** When false, the caller decides which i18n message to show. */
  readonly notAuthorized?: boolean;
}

export interface AuthContextValue {
  /** The InsForge auth user, or null when signed out. */
  readonly user: AuthUser | null;
  /** The matched platform_users row, or null when not a platform super-user. */
  readonly platformUser: PlatformUser | null;
  /** True while the initial session + platform-user lookup is resolving. */
  readonly loading: boolean;
  /** True once a TOTP challenge has been satisfied this session (in memory). */
  readonly mfaPassed: boolean;
  /**
   * The full console access predicate — `user && platformUser && mfaPassed`.
   * The AuthGate decides what RENDERS from it; data hooks gate what they FETCH
   * on it, so no PostgREST read can leave the browser before the session is
   * known good (see {@link useAuthorized}).
   */
  readonly authorized: boolean;
  /**
   * Set when a signed-in auth user has no platform_users row. The shell shows a
   * "No autoritzat" screen; signOut clears it.
   */
  readonly notAuthorized: boolean;
  readonly signIn: (email: string, password: string) => Promise<SignInResult>;
  readonly signOut: () => Promise<void>;
  /** Mark the in-memory MFA gate as satisfied (called by the MFA screen). */
  readonly markMfaPassed: () => void;
  /** Refresh the platform_users row (e.g. after mfa_enabled flips to true). */
  readonly refreshPlatformUser: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

/**
 * Read the `platform_users` row for an auth user id, or null when none.
 * `is_active` is part of the predicate: a deactivated super-user must not pass
 * the gate — RLS (`is_platform_user()`) would deny every tenant row anyway and
 * the console would render as an inexplicably empty directory.
 */
async function fetchPlatformUser(authUserId: string): Promise<PlatformUser | null> {
  const { data, error } = await insforge.database
    .from('platform_users')
    .select('id, auth_user_id, email, role, mfa_enabled')
    .eq('auth_user_id', authUserId)
    .eq('is_active', true)
    .maybeSingle();
  if (error || !data) return null;
  return data as PlatformUser;
}

export function AuthProvider(props: {
  readonly children: React.ReactNode;
}): React.ReactElement {
  const [user, setUser] = React.useState<AuthUser | null>(null);
  const [platformUser, setPlatformUser] = React.useState<PlatformUser | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [mfaPassed, setMfaPassed] = React.useState(false);
  const [notAuthorized, setNotAuthorized] = React.useState(false);

  const clear = React.useCallback(() => {
    setUser(null);
    setPlatformUser(null);
    setMfaPassed(false);
    setNotAuthorized(false);
  }, []);

  // Resolve the existing session on first mount.
  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data } = await insforge.auth.getCurrentUser();
      const authUser = data.user;
      if (!authUser) {
        if (!cancelled) setLoading(false);
        return;
      }
      const pu = await fetchPlatformUser(authUser.id);
      if (cancelled) return;
      if (!pu) {
        // Signed-in but not a platform super-user: deny and drop the session.
        await insforge.auth.signOut();
        if (cancelled) return;
        setNotAuthorized(true);
        setLoading(false);
        return;
      }
      setUser(authUser);
      setPlatformUser(pu);
      // Skip the TOTP challenge when this device is still within its 30-day
      // trust window for this exact platform user.
      if (isDeviceTrusted(pu.id)) setMfaPassed(true);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Session watchdog. The SDK keeps the access token in memory only; when a
  // token refresh fails it clears the session and silently falls back to the
  // ANON key, so every PostgREST read starts returning empty result sets with
  // HTTP 200 while the UI still looks signed in (the "empty tenant directory"
  // bug). Revalidate on window focus and on an interval: `getCurrentUser()`
  // returns the live session (refreshing through the auth cookie when it can),
  // and when the session is definitively gone we drop the local auth state so
  // the AuthGate bounces to /login instead of rendering bogus empty screens.
  React.useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    const revalidate = async (): Promise<void> => {
      const { data, error } = await insforge.auth.getCurrentUser();
      if (cancelled || data.user) return;
      // Only sign out on a definitive auth rejection (or a clean "no session");
      // a transient network failure must not log the operator out.
      const status = error?.statusCode;
      if (!error || status === 401 || status === 403) clear();
    };
    const onFocus = (): void => {
      void revalidate();
    };
    window.addEventListener('focus', onFocus);
    const timer = window.setInterval(() => {
      void revalidate();
    }, 4 * 60_000);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      window.clearInterval(timer);
    };
  }, [user, clear]);

  const signIn = React.useCallback(
    async (email: string, password: string): Promise<SignInResult> => {
      setNotAuthorized(false);
      const { data, error } = await insforge.auth.signInWithPassword({ email, password });
      if (error || !data) {
        return { ok: false };
      }
      const { data: userData } = await insforge.auth.getCurrentUser();
      const authUser = userData.user;
      if (!authUser) {
        return { ok: false };
      }
      const pu = await fetchPlatformUser(authUser.id);
      if (!pu) {
        await insforge.auth.signOut();
        setNotAuthorized(true);
        return { ok: false, notAuthorized: true };
      }
      setUser(authUser);
      setPlatformUser(pu);
      // Honor the 30-day trusted-device cookie here too: without this a fresh
      // password sign-in (e.g. after the InsForge session expired) would always
      // re-challenge MFA, defeating "remember this device". A device WITHOUT a
      // valid cookie still gets the TOTP gate via the route guard.
      setMfaPassed(isDeviceTrusted(pu.id));
      return { ok: true };
    },
    [],
  );

  const signOut = React.useCallback(async (): Promise<void> => {
    await insforge.auth.signOut();
    // NOTE: we deliberately do NOT clear the trusted-device cookie here. It is a
    // 30-day "remember this device" marker (bound to the platform user id) and
    // must survive sign-out — otherwise sharing localhost with the backoffice
    // (whose sign-in clobbers the InsForge session cookie and bounces the console
    // through signOut) would wipe MFA trust and re-challenge on every return.
    // A different user signing in gets a uid mismatch and is challenged anyway.
    clear();
  }, [clear]);

  const markMfaPassed = React.useCallback(() => {
    setMfaPassed(true);
    // Remember this device for 30 days so reloads skip the TOTP challenge. Bound
    // to the platform user id; safe to read from state here because the row is
    // resolved before the MFA gate renders (enrollment only refreshes its flags).
    if (platformUser) trustDevice(platformUser.id);
  }, [platformUser]);

  const refreshPlatformUser = React.useCallback(async (): Promise<void> => {
    if (!user) return;
    const pu = await fetchPlatformUser(user.id);
    if (pu) setPlatformUser(pu);
  }, [user]);

  const authorized = user !== null && platformUser !== null && mfaPassed;

  const value = React.useMemo<AuthContextValue>(
    () => ({
      user,
      platformUser,
      loading,
      mfaPassed,
      authorized,
      notAuthorized,
      signIn,
      signOut,
      markMfaPassed,
      refreshPlatformUser,
    }),
    [
      user,
      platformUser,
      loading,
      mfaPassed,
      authorized,
      notAuthorized,
      signIn,
      signOut,
      markMfaPassed,
      refreshPlatformUser,
    ],
  );

  return <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>;
}

/**
 * The access predicate on its own — for React Query `enabled` flags.
 *
 * A query gated on this NEVER fires while the console is signed out, still
 * resolving the session, or waiting on the TOTP challenge. That matters beyond
 * tidiness: with no session the SDK falls back to the ANON key and a PostgREST
 * read comes back `200 []`, so an ungated read both leaks a pointless request
 * and can paint an empty directory that looks like real data.
 */
export function useAuthorized(): boolean {
  return useAuth().authorized;
}

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
